import { Helmet } from 'react-helmet-async';
import { absoluteUrl, SITE_ORIGIN } from '@/config/site';
import { useLanguage } from '@/i18n/LanguageProvider';
import { SITE_LANG_HINTS } from '@/i18n/meta';

/**
 * SEO Meta Tags Component
 * Usage: <MetaTags title="Page Title" description="Page description" path="/page-path" />
 */
export default function MetaTags({
  title = "ByOnco · The complete navigation platform for cancer care",
  description = "ByOnco is the complete navigation platform for cancer care. We get cancer patients to specialists, the fastest. Understand your diagnosis, match top cancer centers, calculate true costs, and access 24/7 care guidance.",
  path = "/",
  image = `${SITE_ORIGIN}/preview.png?v=7`,
  type = "website",
  jsonLd = null,
}) {
  const url = absoluteUrl(path);
  const fullTitle = path === "/" ? title : `${title} | ByOnco`;
  const schemas = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  const { lang } = useLanguage();
  const languageName = SITE_LANG_HINTS[lang] || 'English';

  return (
    <Helmet htmlAttributes={{ lang: lang || 'en' }}>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="ByOnco" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta name="robots" content="index, follow" />
      <meta name="language" content={languageName} />
      <meta name="author" content="PraesidioCare Private Limited" />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
